import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../../utils/postUrls';
import { useNavigation } from '../../context/NavigationContext';
import {
  Heart,
  MessageSquare,
  Share2,
  Bookmark,
  Send,
  Search,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Flame,
  Clock,
  Calendar,
  Users,
  X,
  Hash,
  AlertCircle,
  Edit3,
  Repeat2,
  Link2,
  ThumbsDown,
  MoreVertical,
  Pin,
  Trash,
  Bell,
  RefreshCw,
  LogOut,
  Flag,
} from "lucide-react";
import { PixelatedTurtle } from '@theme/design-constants';
import { type ThemeTokens } from '@theme/tokens';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { ThemeCustomizerButton, SiteThemeCustomizer } from "./ThemeCustomizer/index";
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import AnimationLayer from "./AnimationLayer";
import { getThemeAnimations } from "@theme/animation-config";
import { cacheCurrentPageTheme } from '../../utils/themeCache';
import { useScale, scaleW, scaleH } from '../../hooks/useScale';
import { useDynamicBackground } from '../../hooks/useDynamicBackground';
import { useSwipe } from '../../hooks/useSwipe';
import { Post as PostType } from '../../types/post';
import { canModeratePost, getEditorType, getPinnerType, User as PermUser } from '../../utils/permissions';
import { EditPostModal } from './EditPostModal';
import { PostBadges } from './PostBadges';
import LoadingScreen from './LoadingScreen';
import MainNavbar from './MainNavbar';
import SoftLoad from './SoftLoad';
import { commentEventBus } from '../../lib/commentEventBus';
import { useCommentSync } from '../../hooks/useCommentSync';
import { notificationEventBus } from '../../lib/notificationEventBus';
import { useNotificationSync } from '../../hooks/useNotificationSync';
import { getBackground, getCardBackground, calculateNavbarBg } from '../../utils/backgroundHelpers';
import { PostCreationModal } from './PostCreation';
import resolveAvatar from '../../lib/resolveAvatar';
import { SubPanelSwiper } from '../mobile/Navigation';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { ReportModal } from '../shared/ReportModal';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
type User = {
  id: string;
  username: string;
  displayName?: string;
  avatar?: string;
  isSiteAdmin?: boolean;
  isSuperAdmin?: boolean;
  isSiteModerator?: boolean;
};

type Reply = {
  id: string;
  content: string;
  author: {
    id: number;
    username: string;
    profileName?: string;
    profilePicture?: string;
  };
  likes: number;
  dislikes: number;
  liked?: boolean;
  disliked?: boolean;
  createdAt: string;
};

type Post = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  title?: string;
  slug?: string;
  content: string;
  mediaUrl?: string;
  imageUrl?: string;
  aboveContent?: boolean;
  belowContent?: boolean;
  tags?: string[];
  likes: number;
  dislikes?: number;
  disliked?: boolean;
  comments: number;
  replies?: Reply[]; // Preview replies for display
  shares: number;
  timestamp: string;
  liked: boolean;
  createdAt: string;
  editedByModeratorId?: number;
  editedByModeratorAt?: string;
  moderatorEditReason?: string;
  communitySlug?: string;
  communityId?: string;
  communityName?: string;
  postType?: 'legacy' | 'community' | 'forum';
  isPinned?: boolean;
  pinnedBy?: string;
  pinnedByType?: 'admin' | 'mod' | 'community_admin' | 'community_mod';
  pinnedAt?: string;
  editedBy?: string;
  editedByType?: 'user' | 'admin' | 'mod' | 'community_admin' | 'community_mod';
  editedAt?: string;
  isPinnedToFeed?: boolean;
  pinnedToFeedAt?: string;
};

type Props = {
  currentUser: User | null;
  initialPosts: Post[];
  isAuthenticated?: boolean;
  mobileMode?: boolean;
  mobileSection?: number;
  onSectionChange?: (index: number) => void;
  mobileScreenWidth?: number;
};

/**
 * Get the correct post URL based on post type
 * Handles routing for legacy, community, and forum posts
 */
function getPostUrl(post: Post): string {
  // Forum posts go to forum post view
  if (post.slug && post.communitySlug && post.postType === 'forum') {
    return getForumPostUrl(post.communitySlug, post.slug);
  }

  // Community posts go to community post view
  if (post.slug && post.communitySlug && post.postType === 'community') {
    return getCommunityPostUrl(post.communitySlug, post.slug);
  }

  // Legacy posts go to user profile post view
  if (post.slug && post.authorUsername) {
    return getLegacyPostUrl(post.authorUsername, post.slug);
  }

  // Fallback for posts without slug
  return getLegacyPostUrl(post.authorUsername || 'unknown', post.slug || post.id.toString());
}

/**
 * Select 3 preview comments based on:
 * 1. Most popular (highest likes)
 * 2. Most controversial (highest dislikes OR closest like/dislike ratio)
 * 3. Random
 */
function selectPreviewComments(replies: Reply[]): Reply[] {
  if (!replies || replies.length === 0) return [];
  if (replies.length <= 3) return replies;

  const selected: Reply[] = [];
  const available = [...replies];

  // 1. Most popular (highest likes)
  const mostPopular = available.reduce((prev, curr) =>
    (curr.likes > prev.likes) ? curr : prev
  );
  selected.push(mostPopular);
  available.splice(available.indexOf(mostPopular), 1);

  // 2. Most controversial (highest dislikes, or if no dislikes, smallest like-dislike difference)
  const mostControversial = available.reduce((prev, curr) => {
    const prevScore = prev.dislikes > 0 ? prev.dislikes : Math.abs(prev.likes - prev.dislikes);
    const currScore = curr.dislikes > 0 ? curr.dislikes : Math.abs(curr.likes - curr.dislikes);
    return currScore > prevScore ? curr : prev;
  });
  selected.push(mostControversial);
  available.splice(available.indexOf(mostControversial), 1);

  // 3. Random
  if (available.length > 0) {
    const randomIndex = Math.floor(Math.random() * available.length);
    selected.push(available[randomIndex]);
  }

  return selected;
}

export default function FeedClient({ currentUser, initialPosts, isAuthenticated = true, mobileMode = false, mobileSection, onSectionChange, mobileScreenWidth = 390 }: Props) {
  const navigate = useNavigate();
  const { theme, currentPreset, siteCustomization, siteAnimations, ensureSiteThemeApplied } = useTheme();
  const { logout, setShowLoginModal } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const rawScale = useScale();
  const scale = mobileMode ? { w: 1, h: 1 } : rawScale;
  const backgroundHeight = useDynamicBackground();

  // Transform initial posts to ensure likes/dislikes are numbers
  const transformedInitialPosts = initialPosts.map(post => {
    return {
      ...post,
      likes: Array.isArray(post.likes) ? post.likes.length : (typeof post.likes === 'number' ? post.likes : 0),
      dislikes: Array.isArray(post.dislikes) ? post.dislikes.length : (typeof post.dislikes === 'number' ? post.dislikes : 0),
      replies: post.replies?.map(comment => ({
        ...comment,
        likes: Array.isArray(comment.likes) ? comment.likes.length : (typeof comment.likes === 'number' ? comment.likes : 0),
        dislikes: Array.isArray(comment.dislikes) ? comment.dislikes.length : (typeof comment.dislikes === 'number' ? comment.dislikes : 0),
      })),
    };
  });

  const [posts, setPosts] = useState<Post[]>(transformedInitialPosts);
  const [isPosting, setIsPosting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("following");
  const [showPostModal, setShowPostModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortBy, setSortBy] = useState<string>("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"profiles" | "communities">("profiles");
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState("");
  const [sidebarSearchType, setSidebarSearchType] = useState<"people" | "communities" | "tags">("people");
  const [showSidebarSearchResults, setShowSidebarSearchResults] = useState(false);
  const [sidebarSearchPeople, setSidebarSearchPeople] = useState<any[]>([]);
  const [sidebarSearchCommunities, setSidebarSearchCommunities] = useState<any[]>([]);
  const [sidebarSearchTags, setSidebarSearchTags] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [followedTags, setFollowedTags] = useState<string[]>([]);
  const [showSiteCustomizer, setShowSiteCustomizer] = useState(false);
  const [isShareAnimating, setIsShareAnimating] = useState(false);
  const [showPostWidget, setShowPostWidget] = useState(false);
  const [followingUsers, setFollowingUsers] = useState<any[]>([]);
  const [followersUsers, setFollowersUsers] = useState<any[]>([]);
  const [friendsUsers, setFriendsUsers] = useState<any[]>([]);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const [themeAnimations, setThemeAnimations] = useState(() => {
    // UNIFIED CACHE: Use animations from ThemeContext (site theme)
    // IMPORTANT: siteAnimations should ONLY contain site theme animations
    // Profile/community themes are managed separately by ProfileClient/CommunityClient
    if (siteAnimations) {
      return siteAnimations;
    }
    // Fallback to preset animations if no site theme customization
    return getThemeAnimations(currentPreset);
  });
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [customization, setCustomization] = useState<any>(
    siteCustomization || {
      textColor: theme.textColor,
      accentColor: theme.accentColor,
      primaryColor: theme.primaryColor,
      secondaryColor: theme.secondaryColor,
      cardBg: theme.cardBg,
      navbarBg: theme.navbarBg,
      tabBarBg: theme.tabBarBg,
      colorSpace: 'Off',
      gradientEnabled: true,
      gradientAngle: 135,
      gradientFavorability: 50,
      gradientStrength: 100,
      temperatureLimit: 50,
      magentaLimit: 50,
      intensityLimit: 50,
      backgroundSaturationLimit: 50,
      backgroundBrightnessLimit: 50,
      redLimit: 50,
      greenLimit: 50,
      blueLimit: 50,
      backgroundContrast: 50,
      backgroundHueGravity: 0,
      backgroundGraySaturation: 0,
      backgroundDefaultHue: 184,
    }
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Focus animation state
  const [focusedPostId, setFocusedPostId] = useState<string | null>(null);
  const [focusedPostIndex, setFocusedPostIndex] = useState<number>(-1);
  const postRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const currentFocusIndexRef = useRef<number>(-1);
  const isThrottledRef = useRef<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const effectiveIsMobile = mobileMode || isMobile;

  // Pull-to-refresh for mobile center column
  const pullToRefreshCallback = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await fetchWithAuth('/api/posts', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const transformed = (data.items || []).map(transformBackendPost);
        setPosts(transformed);
      }
    } catch { /* silent */ }
    setTimeout(() => setIsRefreshing(false), 300);
  }, []);
  const pullToRefresh = usePullToRefresh({ onRefresh: pullToRefreshCallback });

  const [focusAnimationEnabled, setFocusAnimationEnabled] = useState(false);

  // Repost/Share modal state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostPostId, setRepostPostId] = useState<string | null>(null);
  const [repostComment, setRepostComment] = useState('');
  const [isReposting, setIsReposting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState<string | null>(null);
  const [communityPosts, setCommunityPosts] = useState<any[]>([]);
  const [showModerateMenu, setShowModerateMenu] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null);
  const [commentMenuId, setCommentMenuId] = useState<string | null>(null);

  // Close comment menu on outside click
  useEffect(() => {
    if (!commentMenuId) return;
    const handler = () => setCommentMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [commentMenuId]);

  // Draggable modal state
  const [repostModalPosition, setRepostModalPosition] = useState({ x: 0, y: 0 });

  // Register site theme with NavigationContext for LoadingScreen continuity
  useEffect(() => {
    if (!siteCustomization) {
      // Site theme was fetched while a profile/community page was active — apply it now.
      ensureSiteThemeApplied();
      return;
    }
    if (theme?.primaryColor) {
      // Delay registration by 100ms to ensure LoadingScreen reads the previous page's theme
      const timer = setTimeout(() => {
        setCurrentTheme({
          type: 'site',
          colors: {
            primaryColor: theme.primaryColor,
            secondaryColor: theme.secondaryColor,
            textColor: theme.textColor,
            accentColor: theme.accentColor
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [theme?.primaryColor, theme?.secondaryColor, theme?.textColor, theme?.accentColor, siteCustomization, setCurrentTheme, ensureSiteThemeApplied]);
  const [isDraggingRepost, setIsDraggingRepost] = useState(false);
  const [dragStartRepost, setDragStartRepost] = useState({ x: 0, y: 0 });
  const repostModalRef = useRef<HTMLDivElement>(null);
  const [followingPosts, setFollowingPosts] = useState<any[]>([]);
  const [friendsPosts, setFriendsPosts] = useState<any[]>([]);
  const [tagsPosts, setTagsPosts] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Section navigation state
  const [currentSection, setCurrentSection] = useState<'left' | 'middle' | 'right'>('middle');

  // Report section changes to mobile shell
  useEffect(() => {
    if (mobileMode && onSectionChange) {
      const sectionIndex = { left: 0, middle: 1, right: 2 }[currentSection];
      onSectionChange(sectionIndex);
    }
  }, [currentSection, mobileMode, onSectionChange]);

  // Sync mobileSection prop → internal currentSection (for dot indicator taps)
  useEffect(() => {
    if (mobileMode && mobileSection !== undefined) {
      const section = (['left', 'middle', 'right'] as const)[mobileSection];
      if (section && section !== currentSection) {
        setCurrentSection(section);
      }
    }
  }, [mobileMode, mobileSection]);

  // Create local theme with transformed colors for inline styles
  // CRITICAL: Use theme from ThemeContext (already transformed) instead of re-transforming
  const localTheme = React.useMemo(() => {
    // theme from ThemeContext already has computedColors (transformed by buildThemeFromCustomization)
    // We should use it directly instead of transforming again!
    
    const colorSpace = customization.colorSpace || 'Off';

    // Helper to convert hex to rgba with opacity
    const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
      // Validate input
      if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || hex.length < 7) {
        console.error('❌ [FeedClient] Invalid hex color in hexToRgbaWithOpacity:', hex);
        return `rgba(0, 0, 0, ${opacity / 100})`;
      }

      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);

      if (isNaN(r) || isNaN(g) || isNaN(b)) {
        console.error('❌ [FeedClient] Failed to parse hex color (NaN):', { hex, r, g, b });
        return `rgba(0, 0, 0, ${opacity / 100})`;
      }

      return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    };

    // IMPORTANT: theme from ThemeContext already has computed colors (transformed)
    // Theme always has values from preset themes in tokens.ts - no hardcoded fallbacks needed!
    // We just need to handle opacity for navbarBg/tabBarBg/dropdownColor

    // Handle navbar background with opacity
    // Use non-null assertion (!) because theme from ThemeContext always has primaryColor from preset
    const navbarBg = theme.navbarBg || hexToRgbaWithOpacity(theme.primaryColor!, customization.navbarOpacity ?? 92);

    // Handle tab bar background with opacity
    const tabBarBg = theme.tabBarBg || navbarBg;

    // Handle dropdown color (use primary color from theme if not custom)
    const dropdownColor = customization.dropdownColor
      ? (colorSpace === 'Off' ? customization.dropdownColor : theme.primaryColor!)
      : theme.primaryColor!;

    
    // Return theme from ThemeContext with custom opacity settings
    // Theme from ThemeContext always has complete values from preset themes - no overrides needed
    // Type assertion: theme from ThemeContext always has these required properties from preset
    return {
      ...theme,
      primaryColor: theme.primaryColor!, // Always defined from preset theme
      secondaryColor: theme.secondaryColor!, // Always defined from preset theme
      navbarBg,
      tabBarBg,
      dropdownColor,
    } as ThemeTokens & { primaryColor: string; secondaryColor: string };
  }, [theme, customization.navbarOpacity, customization.tabBarOpacity, customization.dropdownColor, customization.colorSpace]);

  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, opacity: number): string => {
    if (hex.startsWith('rgba')) {
      const match = hex.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
      }
      return hex;
    }

    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Helper to get dropdown button background with custom opacity
  const getDropdownButtonBg = () => {
    const baseColor = localTheme.dropdownColor || localTheme.tabBarBg || localTheme.primaryColor;
    const opacity = (customization.dropdownButtonOpacity ?? 100) / 100;
    return hexToRgba(baseColor, opacity);
  };

  // Helper to get dropdown menu background with custom opacity
  const getDropdownMenuBg = () => {
    const baseColor = localTheme.dropdownColor || localTheme.tabBarBg || localTheme.primaryColor;
    const opacity = (customization.dropdownOpacity ?? 95) / 100;
    return hexToRgba(baseColor, opacity);
  };

  // Use localTheme directly instead of recalculating
  const themeColors = {
    navbarBg: localTheme.navbarBg,
    accentColor: localTheme.accentColor,
    textColor: localTheme.textColor,
    tabBarBg: localTheme.tabBarBg,
    dropdownButtonBg: getDropdownButtonBg(),
    dropdownMenuBg: getDropdownMenuBg(),
    gradientFrom: localTheme.primaryColor || customization.gradientFrom,
    gradientTo: localTheme.secondaryColor || customization.gradientTo
  };

  // Apply customization as CSS variables when localTheme changes
  React.useEffect(() => {
    const root = document.documentElement;

    // Helper to blend two colors
    const blendColors = (color1: string, color2: string, ratio: number) => {
      const r1 = parseInt(color1.slice(1, 3), 16);
      const g1 = parseInt(color1.slice(3, 5), 16);
      const b1 = parseInt(color1.slice(5, 7), 16);

      const r2 = parseInt(color2.slice(1, 3), 16);
      const g2 = parseInt(color2.slice(3, 5), 16);
      const b2 = parseInt(color2.slice(5, 7), 16);

      const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
      const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
      const b = Math.round(b1 * (1 - ratio) + b2 * ratio);

      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Apply background gradient
    if (customization.gradientEnabled) {
      const ratio = (customization.gradientFavorability || 50) / 100;
      const blendedColor1 = blendColors(localTheme.primaryColor, localTheme.secondaryColor, ratio);
      const blendedColor2 = blendColors(localTheme.secondaryColor, localTheme.primaryColor, ratio);
      const strength = (customization.gradientStrength || 100) / 100;
      const finalColor1 = blendColors('#808080', blendedColor1, strength);
      const finalColor2 = blendColors('#808080', blendedColor2, strength);

      root.style.setProperty('--app-bg', `linear-gradient(${customization.gradientAngle || 135}deg, ${finalColor1}, ${finalColor2})`);
    } else {
      root.style.setProperty('--app-bg', localTheme.primaryColor);
    }

    // Apply other CSS variables (using transformed colors from localTheme)
    root.style.setProperty('--color-text', localTheme.textColor);
    root.style.setProperty('--color-accent', localTheme.accentColor);
    root.style.setProperty('--navbar-bg', localTheme.navbarBg || '');
    root.style.setProperty('--tab-bar-bg', localTheme.tabBarBg || '');

    // Apply card background with gradient if enabled
    if (customization.cardGradientEnabled) {
      const ratio = (customization.cardGradientFavorability || 50) / 100;
      // CRITICAL: Use localTheme.cardGradientColor1/2 (transformed from ThemeContext)
      // ThemeContext now properly returns transformed cardGradientColor1/2 from preset themes
      const blendedCard1 = blendColors(localTheme.cardGradientColor1!, localTheme.cardGradientColor2!, ratio);
      const blendedCard2 = blendColors(localTheme.cardGradientColor2!, localTheme.cardGradientColor1!, ratio);
      const strength = (customization.cardGradientStrength || 100) / 100;
      const finalCard1 = blendColors(localTheme.cardBg, blendedCard1, strength);
      const finalCard2 = blendColors(localTheme.cardBg, blendedCard2, strength);

      const gradientBg = `linear-gradient(${customization.cardGradientAngle || 120}deg, ${hexToRgba(finalCard1, customization.cardOpacity || 100)}, ${hexToRgba(finalCard2, customization.cardOpacity || 100)})`;
      root.style.setProperty('--card-bg', gradientBg);
    } else {
      root.style.setProperty('--card-bg', hexToRgba(localTheme.cardBg, customization.cardOpacity || 100));
    }

    // Cache the transformed theme for instant loading on next page load
    const appBg = customization.gradientEnabled
      ? root.style.getPropertyValue('--app-bg')
      : localTheme.primaryColor;

    const cardBg = root.style.getPropertyValue('--card-bg');

    cacheCurrentPageTheme({
      textColor: localTheme.textColor,
      accentColor: localTheme.accentColor,
      cardBg: cardBg || localTheme.cardBg,
      appBg: appBg || localTheme.primaryColor,
      navbarBg: localTheme.navbarBg || '',
      tabBarBg: localTheme.tabBarBg || '',
      primaryColor: localTheme.primaryColor,
      secondaryColor: localTheme.secondaryColor,
      dropdownColor: localTheme.dropdownColor || '',
      cardGradientColor1: localTheme.cardGradientColor1,
      cardGradientColor2: localTheme.cardGradientColor2,
      colorSpace: customization.colorSpace || 'Off', // CRITICAL: Include colorSpace in cache
      themeType: 'site', // REQUIRED: themeType property for CachedThemeData
      pageType: 'feed',
      userId: currentUser?.id?.toString()
    });
  }, [localTheme, customization, currentUser?.id]);

  // Swipe handlers
  const swipeRef = useSwipe({
    onSwipeLeft: () => {
      if (currentSection === 'middle') setCurrentSection('right');
      else if (currentSection === 'left') setCurrentSection('middle');
    },
    onSwipeRight: () => {
      if (currentSection === 'middle') setCurrentSection('left');
      else if (currentSection === 'right') setCurrentSection('middle');
    }
  });

  // Comment synchronization across all pages
  useCommentSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === data.commentId.toString()
                ? { ...r, liked: data.liked, likes: data.likes }
                : r
            ),
          };
        }
        return p;
      }));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === data.commentId.toString()
                ? { ...r, disliked: data.disliked, dislikes: data.dislikes }
                : r
            ),
          };
        }
        return p;
      }));
    },
  });

  // Notification synchronization for real-time updates
  useNotificationSync({
    onNew: (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onRead: (notificationId) => {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    },
    onReadAll: () => {
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    },
    onDelete: (notificationId) => {
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    },
  });

  // Watch for siteCustomization updates from ThemeContext
  useEffect(() => {
    if (siteCustomization) {
            setCustomization(siteCustomization);
    }
  }, [siteCustomization]);

  // Fetch following, followers, and friends users
  useEffect(() => {
    async function loadFollowingAndFollowers() {
      try {
        const { getFollowing, getFollowers, getFriends } = await import('../../lib/api-client');
        const [following, followers, friends] = await Promise.all([
          getFollowing().catch(() => []),
          getFollowers().catch(() => []),
          getFriends().catch(() => [])
        ]);
        setFollowingUsers(following);
        setFollowersUsers(followers);
        setFriendsUsers(friends);
      } catch (error) {
        setFollowingUsers([]);
        setFollowersUsers([]);
        setFriendsUsers([]);
      }
    }

    loadFollowingAndFollowers();

    // Listen for follow/unfollow events to refresh the list
    const handleFollowingUpdated = () => {
      loadFollowingAndFollowers();
    };

    window.addEventListener('followingUpdated', handleFollowingUpdated);
    return () => window.removeEventListener('followingUpdated', handleFollowingUpdated);
  }, []);

  // Sync animations when siteAnimations or currentPreset changes
  useEffect(() => {
    console.log('[FeedClient] 🎬 Animation sync triggered. siteAnimations:', siteAnimations);
    if (siteAnimations) {
      console.log('[FeedClient] 🎬 Setting themeAnimations from siteAnimations');
      setThemeAnimations(siteAnimations);
    } else {
      console.log('[FeedClient] 🎬 Setting themeAnimations from preset:', currentPreset);
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [siteAnimations, currentPreset]);

  // Listen for theme updates from customizer
  useEffect(() => {
    const handleThemeUpdate = (event: any) => {
      const { animations, animationsEnabled: enabled, customization, baseTheme } = event.detail;

      // Merge incoming partial animations with full defaults from the appropriate theme
      if (animations) {
        // Use baseTheme from event if available, otherwise fall back to currentPreset
        const themeToUse = baseTheme || currentPreset;

        // Get the full default animations for the appropriate theme
        const defaultAnimations = getThemeAnimations(themeToUse);

        // Merge partial animation data with defaults
        const mergedAnimations = {
          // Merge characters array - match by id and merge properties
          characters: defaultAnimations.characters.map((defaultChar) => {
            const customChar = animations.characters?.find((c: any) => c.id === defaultChar.id);
            return customChar ? { ...defaultChar, ...customChar } : defaultChar;
          }),

          // Merge bubbles object
          bubbles: animations.bubbles
            ? { ...defaultAnimations.bubbles, ...animations.bubbles }
            : defaultAnimations.bubbles,

          // Merge decorations array - match by id and merge properties
          decorations: defaultAnimations.decorations.map((defaultDeco) => {
            const customDeco = animations.decorations?.find((d: any) => d.id === defaultDeco.id);
            return customDeco ? { ...defaultDeco, ...customDeco } : defaultDeco;
          }),
        };

        setThemeAnimations(mergedAnimations);
      }

      if (typeof enabled === 'boolean') {
        setAnimationsEnabled(enabled);
      }

      if (customization) {
        setCustomization(customization);
        if (typeof customization.focusAnimationEnabled === 'boolean') {
          setFocusAnimationEnabled(customization.focusAnimationEnabled);
        }
      }
    };

    window.addEventListener('siteThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('siteThemeUpdated', handleThemeUpdate);
  }, [currentPreset]);

  // Responsive check
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Search functionality for Navigate widget
  useEffect(() => {
    const performSearch = async () => {
      if (!sidebarSearchQuery.trim()) {
        setSidebarSearchPeople([]);
        setSidebarSearchCommunities([]);
        setSidebarSearchTags([]);
        setShowSidebarSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        if (sidebarSearchType === 'people') {
          const response = await fetchWithAuth(`/api/search/people?q=${encodeURIComponent(sidebarSearchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSidebarSearchPeople(data.people || []);
          }
        } else if (sidebarSearchType === 'communities') {
          const response = await fetchWithAuth(`/api/search/communities?q=${encodeURIComponent(sidebarSearchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSidebarSearchCommunities(data.communities || []);
          }
        } else if (sidebarSearchType === 'tags') {
          const response = await fetchWithAuth(`/api/search/tags?q=${encodeURIComponent(sidebarSearchQuery)}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setSidebarSearchTags(data.tags || []);
            // Auto-show results for tags
            setShowSidebarSearchResults(true);
          }
        }
      } catch (error) {
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [sidebarSearchQuery, sidebarSearchType]);

  // Helper to transform backend posts to frontend format
  const transformBackendPost = (post: any): Post => {
    // Debug: Check if replies exist in raw post data
    if (post.id === posts[0]?.id || Math.random() < 0.1) {
    }

    // Normalize likes/dislikes to numeric counts (backend may send arrays of {userId})
    const likesCount = Array.isArray(post.likes) ? post.likes.length : (typeof post.likes === 'number' ? post.likes : 0);
    const dislikesCount = Array.isArray(post.dislikes) ? post.dislikes.length : (typeof post.dislikes === 'number' ? post.dislikes : 0);

    // Transform replies if they exist
    const replies = post.replies?.map((comment: any) => ({
      id: comment.id?.toString() || comment.id,
      content: comment.content,
      author: comment.author,
      likes: typeof comment.likes === 'number' ? comment.likes : 0,
      dislikes: typeof comment.dislikes === 'number' ? comment.dislikes : 0,
      createdAt: comment.createdAt,
    }));

    return {
      id: post.id,
      // Handle both formats: authorName (from feed) or author object (from /posts endpoint)
      author: post.authorName || (typeof post.author === 'string' ? post.author : (post.author?.profileName || post.author?.name || post.author?.username)) || 'Unknown',
      authorId: post.authorId || post.author?.id || 'Unknown',
      authorAvatar: resolveAvatar(post.authorAvatar || post.author?.profilePicture || post.author?.image),
      authorUsername: post.authorUsername || post.author?.username,
      slug: post.slug,
      title: post.title,
      content: post.content,
      mediaUrl: post.mediaUrl || post.imageUrl,
      aboveContent: post.aboveContent,
      belowContent: post.belowContent,
      tags: post.tags || [],
      likes: likesCount,
      dislikes: dislikesCount,
      disliked: post.disliked || false,
      comments: post.comments || post._count?.comments || post._count?.replies || 0,
      shares: 0,
      timestamp: post.timestamp || (post.createdAt ? new Date(post.createdAt).toLocaleString() : 'Unknown'),
      liked: post.liked || false,
      createdAt: typeof post.createdAt === 'string' ? post.createdAt : (post.createdAt ? new Date(post.createdAt).toISOString() : new Date().toISOString()),
      editedByModeratorId: post.editedByModeratorId,
      editedByModeratorAt: typeof post.editedByModeratorAt === 'string' ? post.editedByModeratorAt : (post.editedByModeratorAt ? new Date(post.editedByModeratorAt).toISOString() : undefined),
      moderatorEditReason: post.moderatorEditReason,
      communitySlug: post.communitySlug,
      communityName: post.communityName,
      postType: post.postType,
      replies: replies,
    };
  };

  // Fetch posts based on active tab
  useEffect(() => {
    async function fetchFilteredPosts() {
      try {
        if (activeTab === 'community') {
          const response = await fetchWithAuth('/api/communities/feed', {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            const transformed = (data.posts || []).map(transformBackendPost);
            setCommunityPosts(transformed);
          }
        } else if (activeTab === 'following') {
          const response = await fetchWithAuth('/api/posts/following', {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            // Debug: Check if API response has replies
            if (data.items?.[0]) {
            }
            const transformed = (data.items || []).map(transformBackendPost);
            setFollowingPosts(transformed);
          }
        } else if (activeTab === 'friends') {
          const response = await fetchWithAuth('/api/posts/friends', {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            // Debug: Check if API response has replies
            if (data.items?.[0]) {
            }
            const transformed = (data.items || []).map(transformBackendPost);
            setFriendsPosts(transformed);
          }
        } else if (activeTab === 'tags') {
          const response = await fetchWithAuth('/api/posts/tags', {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            const transformed = (data.items || []).map(transformBackendPost);
            setTagsPosts(transformed);
          }
        }
      } catch (error) {
      }
    }
    fetchFilteredPosts();
  }, [activeTab]);

  // Focus animation effect - scroll-based post focusing
  useEffect(() => {
    if (!focusAnimationEnabled) {
      setFocusedPostId(null);
      setFocusedPostIndex(-1);
      return;
    }

    if (posts.length === 0) return;

    const handleWheel = (e: WheelEvent) => {
      const direction = e.deltaY > 0 ? 1 : -1;
      const currentIndex = currentFocusIndexRef.current;

      // Check if we're at the edges
      const atTop = currentIndex === 0 && direction === -1;
      const atBottom = currentIndex === posts.length - 1 && direction === 1;

      // Allow normal scrolling at edges
      if (atTop || atBottom) {
        return;
      }

      e.preventDefault();

      // Update immediately without throttle
      const newIndex = currentIndex + direction;
      currentFocusIndexRef.current = newIndex;

      const focusedPost = posts[newIndex];
      if (focusedPost) {
        setFocusedPostId(focusedPost.id);
        setFocusedPostIndex(newIndex);
      }
    };

    // Initialize first post as focused (only if not already set)
    if (currentFocusIndexRef.current === -1 || currentFocusIndexRef.current >= posts.length) {
      currentFocusIndexRef.current = 0;
    }

    if (posts[currentFocusIndexRef.current]) {
      setFocusedPostId(posts[currentFocusIndexRef.current].id);
      setFocusedPostIndex(currentFocusIndexRef.current);
    }

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [focusAnimationEnabled, posts]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();

    // Listen for real-time notifications via Socket.IO
    // Note: Socket connection should already exist in the app
    // This is just a placeholder - actual socket implementation may vary
  }, []);

  const likePost = useCallback(async (postId: string) => {
    // Find the post and store initial state
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    const initialLiked = post.liked;
    const initialLikes = post.likes;

    // Optimistic update
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id === postId) {
          return {
            ...p,
            liked: !initialLiked,
            likes: initialLiked ? initialLikes - 1 : initialLikes + 1,
          };
        }
        return p;
      })
    );

    try {
      const response = await fetchWithAuth(`/api/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state on error
        setPosts((prev) =>
          prev.map((p) => {
            if (p.id === postId) {
              return {
                ...p,
                liked: initialLiked,
                likes: initialLikes,
              };
            }
            return p;
          })
        );
      }
    } catch (_error) {
      // Revert to initial state on error
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id === postId) {
            return {
              ...p,
              liked: initialLiked,
              likes: initialLikes,
            };
          }
          return p;
        })
      );
    }
  }, [posts]);

  const dislikePost = useCallback(async (postId: string) => {
    // Find the post and store initial state
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    const initialDisliked = post.disliked;
    const initialDislikes = post.dislikes || 0;

    // Optimistic update
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id === postId) {
          return {
            ...p,
            disliked: !initialDisliked,
            dislikes: initialDisliked ? initialDislikes - 1 : initialDislikes + 1,
          };
        }
        return p;
      })
    );

    try {
      const response = await fetchWithAuth(`/api/posts/${postId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state on error
        setPosts((prev) =>
          prev.map((p) => {
            if (p.id === postId) {
              return {
                ...p,
                disliked: initialDisliked,
                dislikes: initialDislikes,
              };
            }
            return p;
          })
        );
      }
    } catch (_error) {
      // Revert to initial state on error
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id === postId) {
            return {
              ...p,
              disliked: initialDisliked,
              dislikes: initialDislikes,
            };
          }
          return p;
        })
      );
    }
  }, [posts]);

  const likeComment = async (commentId: string, postId: string) => {
    // Find the post and comment
    const post = posts.find(p => p.id === postId);
    if (!post || !post.replies) return;

    const comment = post.replies.find((r: any) => r.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialLiked = comment.liked || false;
    const initialLikes = comment.likes || 0;

    // Optimistic update
    const newLiked = !initialLiked;
    const newLikes = initialLiked ? initialLikes - 1 : initialLikes + 1;

    setPosts(prev => prev.map(p => {
      if (p.id === postId && p.replies) {
        return {
          ...p,
          replies: p.replies.map((r: any) =>
            r.id === commentId
              ? { ...r, liked: newLiked, likes: newLikes }
              : r
          ),
        };
      }
      return p;
    }));

    // Emit event bus for same-page sync
    commentEventBus.emitLike(parseInt(commentId), newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/comments/${commentId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts(prev => prev.map(p => {
          if (p.id === postId && p.replies) {
            return {
              ...p,
              replies: p.replies.map((r: any) =>
                r.id === commentId
                  ? { ...r, liked: initialLiked, likes: initialLikes }
                  : r
              ),
            };
          }
          return p;
        }));
        commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
      }
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.id === postId && p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === commentId
                ? { ...r, liked: initialLiked, likes: initialLikes }
                : r
            ),
          };
        }
        return p;
      }));
      commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
    }
  };

  const dislikeComment = async (commentId: string, postId: string) => {
    // Find the post and comment
    const post = posts.find(p => p.id === postId);
    if (!post || !post.replies) return;

    const comment = post.replies.find((r: any) => r.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialDisliked = comment.disliked || false;
    const initialDislikes = comment.dislikes || 0;

    // Optimistic update
    const newDisliked = !initialDisliked;
    const newDislikes = initialDisliked ? initialDislikes - 1 : initialDislikes + 1;

    setPosts(prev => prev.map(p => {
      if (p.id === postId && p.replies) {
        return {
          ...p,
          replies: p.replies.map((r: any) =>
            r.id === commentId
              ? { ...r, disliked: newDisliked, dislikes: newDislikes }
              : r
          ),
        };
      }
      return p;
    }));

    // Emit event bus for same-page sync
    commentEventBus.emitDislike(parseInt(commentId), newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/comments/${commentId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts(prev => prev.map(p => {
          if (p.id === postId && p.replies) {
            return {
              ...p,
              replies: p.replies.map((r: any) =>
                r.id === commentId
                  ? { ...r, disliked: initialDisliked, dislikes: initialDislikes }
                  : r
              ),
            };
          }
          return p;
        }));
        commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.id === postId && p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === commentId
                ? { ...r, disliked: initialDisliked, dislikes: initialDislikes }
                : r
            ),
          };
        }
        return p;
      }));
      commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
    }
  };

  // Calculate post style based on distance from focused post (pyramid/octagon effect)
  const getPostStyle = (postIndex: number) => {
    // Calculate the container width (32vw)
    const containerWidth = window.innerWidth * 0.32;

    if (!focusAnimationEnabled || focusedPostIndex === -1) {
      // Focus animation is disabled - use full container width
      return {
        width: `${containerWidth}px`,
        maxWidth: `${containerWidth}px`,
        transform: 'scale(1)',
        transformOrigin: 'center center',
        opacity: 1,
        willChange: 'transform, width, opacity',
        transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      };
    }

    const distance = Math.abs(postIndex - focusedPostIndex);
    const baseWidth = containerWidth; // Use container width as base

    // Focused post is full base width
    if (distance === 0) {
      return {
        width: `${baseWidth}px`,
        maxWidth: `${baseWidth}px`,
        transform: 'scale(1.05)',
        transformOrigin: 'center center',
        opacity: 1,
        willChange: 'transform, width, opacity',
        transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      };
    }

    // Even size reduction with 12% steps, 72% maximum
    const widthPercent = Math.max(72, 100 - (distance * 12));
    const calculatedWidth = (baseWidth * widthPercent) / 100;

    // Calculate scale - subtle scaling for smooth effect
    const scale = Math.max(0.92, 1 - (distance * 0.02));

    // Calculate opacity reduction
    const opacity = Math.max(0.5, 1 - (distance * 0.12));

    return {
      width: `${calculatedWidth}px`,
      maxWidth: `${calculatedWidth}px`,
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      opacity,
      willChange: 'transform, width, opacity',
      transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    };
  };

  const handleCreatePost = async (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
  }) => {
    if (!postData.content.trim() || isPosting || !currentUser) return;

    setIsPosting(true);

    const optimisticPost: Post = {
      id: `temp-${Date.now()}`,
      author: currentUser.displayName || currentUser.username,
      authorId: currentUser.id,
      authorAvatar: resolveAvatar(currentUser.avatar),
      title: postData.title.trim() || undefined,
      content: postData.content,
      tags: postData.tags,
      likes: 0,
      comments: 0,
      shares: 0,
      timestamp: "now",
      liked: false,
      createdAt: new Date().toISOString(),
      mediaUrl: postData.mediaUrl || undefined,
    };

    setPosts(prev => [optimisticPost, ...prev]);

    try {
      const response = await fetchWithAuth('/api/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: postData.title.trim() || undefined,
          content: postData.content,
          tags: postData.tags,
          visibility: 'public',
          imageUrl: postData.mediaUrl || undefined,
          mediaPosition: postData.mediaPosition
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const realPost = data.post; // Your API returns { post: Post }
        
        // Transform the API response to match our Post type
        const transformedPost: Post = {
          id: realPost.id.toString(),
          author: realPost.author.profileName || realPost.author.name || realPost.author.username,
          authorId: realPost.author.id.toString(),
          authorUsername: realPost.author.username,
          authorAvatar: resolveAvatar(realPost.author.profilePicture || realPost.author.image),
          title: realPost.title,
          slug: realPost.slug,
          content: realPost.content,
          tags: realPost.tags || [],
          likes: 0, // New posts start with 0 likes
          comments: 0, // New posts start with 0 comments
          shares: 0,
          timestamp: "now",
          liked: false,
          createdAt: realPost.createdAt,
        };

        setPosts(prev => prev.map(p => 
          p.id === optimisticPost.id ? transformedPost : p
        ));
      } else {
        // Remove optimistic post on error
        setPosts(prev => prev.filter(p => p.id !== optimisticPost.id));
        const errorData = await response.json();
        alert(errorData.error || 'Failed to create post');
      }
    } catch (_error) {
      // Remove optimistic post on error
      setPosts(prev => prev.filter(p => p.id !== optimisticPost.id));
      alert('Network error. Please try again.');
    }
    
    setIsPosting(false);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Fetch fresh posts data
      const response = await fetchWithAuth('/api/posts', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const transformed = (data.items || []).map(transformBackendPost);
        setPosts(transformed);
      }
    } catch (error) {
    }
    // Brief delay to show loading screen
    setTimeout(() => {
      setIsRefreshing(false);
    }, 300);
  };

  const handleLogout = () => {
    logout();
  };

  // Note: PixelatedTurtle is imported from @theme/design-constants at the top
  // It already accepts a color prop

  const PixelatedCoral = ({ size = 32 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" className="pixelated">
      <g fill="#f97316">
        <rect x="14" y="8" width="4" height="4" />
        <rect x="12" y="12" width="8" height="4" />
        <rect x="10" y="16" width="4" height="4" />
        <rect x="18" y="16" width="4" height="4" />
        <rect x="14" y="20" width="4" height="4" />
      </g>
    </svg>
  );

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'following', label: 'Following' },
    { id: 'friends', label: 'Friends' },
    { id: 'community', label: 'Community' },
    { id: 'tags', label: 'Tags' }
  ];

  const navItems = [
    { label: 'Home', path: '/feed' },
    { label: 'Friends', path: '/friends' },
    { label: 'Messages', path: '/messages' },
    { label: 'Community', path: '/community' },
    { label: 'Ads', path: '/ads' },
    { label: 'Marketplace', path: '/marketplace' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest', icon: Calendar },
    { value: 'oldest', label: 'Oldest', icon: Clock },
    { value: 'popular', label: 'Popular', icon: TrendingUp },
    { value: 'unpopular', label: 'Unpopular', icon: TrendingDown },
    { value: 'controversial', label: 'Most Controversial', icon: Flame }
  ];

  const currentSortOption = sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

  const handleRepost = useCallback((postId: string) => {
    setRepostPostId(postId);
    setShowRepostModal(true);
  }, []);

  const handleToggleFollowTag = async (tag: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking follow button

    const isCurrentlyFollowing = followedTags.includes(tag);
    const newFollowingState = !isCurrentlyFollowing;

    // Optimistically update UI
    setFollowedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );

    try {
      const response = await fetchWithAuth(`/api/tags/${tag}/follow`, {
        method: newFollowingState ? 'POST' : 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert on error
        setFollowedTags(prev =>
          prev.includes(tag)
            ? prev.filter(t => t !== tag)
            : [...prev, tag]
        );
      }
    } catch (error) {
      // Revert on error
      setFollowedTags(prev =>
        prev.includes(tag)
          ? prev.filter(t => t !== tag)
          : [...prev, tag]
      );
    }
  };

  const handleSubmitRepost = async () => {
    if (!currentUser || !repostPostId) return;

    setIsReposting(true);
    try {
      const response = await fetchWithAuth(`/api/posts/${repostPostId}/repost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: repostComment || undefined })
      });

      if (response.ok) {
        setShowRepostModal(false);
        setRepostComment('');
        setRepostPostId(null);
        window.location.reload();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to repost');
      }
    } catch (_error) {
      alert('Network error. Please try again.');
    }
    setIsReposting(false);
  };

  const handleShare = useCallback(async (postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    const url = post.slug && post.authorUsername
      ? `${window.location.origin}${getLegacyPostUrl(post.authorUsername, post.slug)}`
      : `${window.location.origin}/posts/${postId}`;

    // On mobile, try native share API first
    if (effectiveIsMobile && navigator.share) {
      try {
        await navigator.share({ title: post.title || 'Check out this post', url });
        fetchWithAuth(`/api/posts/${postId}/link-share`, { method: 'POST', credentials: 'include' }).catch(() => {});
        return;
      } catch { /* user cancelled or not supported, fall through to modal */ }
    }

    setSharePostId(postId);
    setShowShareModal(true);
  }, [posts, effectiveIsMobile]);

  const handleCopyLink = async () => {
    if (!sharePostId) return;
    const post = posts.find(p => p.id === sharePostId);
    if (!post) return;

    // Use slug-based URL if available
    const url = post.slug && post.authorUsername
      ? `${window.location.origin}${getLegacyPostUrl(post.authorUsername, post.slug)}`
      : `${window.location.origin}/posts/${sharePostId}`;

    try {
      await navigator.clipboard.writeText(url);

      // Increment link share count
      await fetchWithAuth(`/api/posts/${sharePostId}/link-share`, {
        method: 'POST',
        credentials: 'include'
      });

      setShowShareModal(false);
      setSharePostId(null);
    } catch (err) {
      // silent fail on mobile
    }
  };

  const handleEditPost = useCallback((post: Post) => {
    setEditingPost(post);
    setShowModerateMenu(null);
  }, []);

  const handleSaveEdit = async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!editingPost) return;
    if (!currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const editorType = getEditorType(editingPost, permUser);
    const endpoint = editingPost.postType === 'community'
      ? `/api/moderation/communities/${editingPost.communityId}/posts/${editingPost.id}`
      : `/api/moderation/posts/${editingPost.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        const mappedEditorType = editorType === 'moderator' ? 'mod' : editorType === 'community_moderator' ? 'community_mod' : editorType;
        setPosts(prev => prev.map(p => p.id === editingPost.id ? {
          ...p,
          ...updatedData,
          editedBy: currentUser.id.toString(),
          editedByType: mappedEditorType as 'user' | 'admin' | 'mod' | 'community_admin' | 'community_mod',
          editedAt: new Date().toISOString()
        } : p));
        setEditingPost(null);
      }
    } catch (error) {
      throw error;
    }
  };

  const handlePinPost = useCallback(async (post: Post) => {
    if (!currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const pinnerType = getPinnerType(post, permUser);
    const endpoint = post.postType === 'community'
      ? `/api/moderation/communities/${post.communityId}/posts/${post.id}/pin`
      : `/api/moderation/posts/${post.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const mappedPinnerType = pinnerType === 'moderator' ? 'mod' : pinnerType === 'community_moderator' ? 'community_mod' : pinnerType;
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinned: data.pinned,
          pinnedBy: data.pinned ? currentUser.id.toString() : undefined,
          pinnedByType: data.pinned ? mappedPinnerType as 'admin' | 'mod' | 'community_admin' | 'community_mod' : undefined,
          pinnedAt: data.pinned ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, [currentUser]);

  const handlePinToFeed = useCallback(async (post: Post) => {
    if (!(currentUser as any).isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${post.id}/pin-to-feed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinnedToFeed: data.pinnedToFeed,
          pinnedToFeedAt: data.pinnedToFeed ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, [currentUser]);

  const handleDeletePost = useCallback(async (post: Post) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const endpoint = post.postType === 'community'
      ? `/api/moderation/communities/${post.communityId}/posts/${post.id}`
      : `/api/moderation/posts/${post.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setPosts(prev => prev.filter(p => p.id !== post.id));
        setShowModerateMenu(null);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete post');
      }
    } catch (error) {
      alert('Network error. Please try again.');
    }
  }, []);

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'DISLIKE':
        return <ThumbsDown size={20} style={{ color: localTheme.textColor }} />;
      case 'REPOST':
        return <Repeat2 size={20} style={{ color: localTheme.textColor }} />;
      case 'MESSAGE':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      case 'DISLIKE':
        return `${actor} disliked your post`;
      case 'REPOST':
        return `${actor} reposted your post`;
      case 'MESSAGE':
        return `${actor} sent you a message`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        // Use actorUsername instead of actorId
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`); // Fallback
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        // Route based on postType and slug
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed'); // Fallback
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  };

  // Toggle handlers
  const handleToggleShareAnimation = useCallback(() => {
    setTimeout(() => {
      setIsShareAnimating(true);
      setTimeout(() => {
        setShowPostWidget(true);
        setIsShareAnimating(false);
      }, 300);
    }, 0);
  }, []);

  const handleSetActiveTab = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  const handleToggleSortMenu = useCallback(() => {
    setShowSortMenu(prev => !prev);
  }, []);

  const handleCloseSortMenu = useCallback(() => {
    setShowSortMenu(false);
  }, []);

  const handleSortChange = useCallback((value: string) => {
    setSortBy(value);
    setShowSortMenu(false);
  }, []);

  const handleToggleModerateMenu = useCallback((postId: string | null) => {
    setShowModerateMenu(prev => prev === postId ? null : postId);
  }, []);

  const handleCloseModerateMenu = useCallback(() => {
    setShowModerateMenu(null);
  }, []);

  const handleNavigateToPost = useCallback((post: Post) => {
    navigate(getPostUrl(post));
  }, [navigate]);

  const handleSetSidebarSearchType = useCallback((type: 'people' | 'communities' | 'tags') => {
    setSidebarSearchType(type);
  }, []);

  // ─── Column Content (shared between desktop & mobile layouts) ───

  const leftColumnContent = (
    <div className={effectiveIsMobile ? "flex flex-col items-center gap-3 pt-6" : ""}>
      {/* Share Button (Animated) - Only show when authenticated */}
      {isAuthenticated && (
        <button
          onClick={handleToggleShareAnimation}
          className="rounded-lg shadow-lg font-semibold overflow-hidden"
          style={{
            backgroundColor: localTheme.accentColor,
            color: localTheme.textColor,
            padding: effectiveIsMobile
              ? '16px 48px'
              : `${scaleH(12, scale.h)}px ${scaleW(32, scale.w)}px`,
            fontSize: effectiveIsMobile ? '18px' : `${scaleW(18, scale.w)}px`,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: showPostWidget ? 'scale(1.05)' : 'scale(1)',
            width: effectiveIsMobile ? 'auto' : undefined,
          }}
        >
          <span
            style={{
              display: 'inline-block',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: isShareAnimating ? 0 : 1,
              transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
            }}
          >
            {showPostWidget ? 'Post' : 'Share'}
          </span>
        </button>
      )}
      {effectiveIsMobile && (
        <p className="text-xs text-center" style={{ color: localTheme.textColor, opacity: 0.4 }}>
          Tap to create a post
        </p>
      )}
    </div>
  );

  const renderCenterColumn = () => (
    <>
        {/* Feed Tabs Card */}
        <div className="rounded-lg backdrop-blur-sm border mb-6 sticky" style={{
          background: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
          borderColor: `${localTheme.accentColor}40`,
          zIndex: 40,
          width: effectiveIsMobile ? '100%' : 'fit-content',
          paddingLeft: effectiveIsMobile ? '8px' : `${scaleW(24, scale.w)}px`,
          paddingRight: effectiveIsMobile ? '8px' : `${scaleW(24, scale.w)}px`,
          top: effectiveIsMobile ? '0px' : `${scaleH(100, scale.h)}px`
        }}>
          <div className="flex items-center" style={{ justifyContent: effectiveIsMobile ? 'space-between' : undefined }}>
            <div className="flex items-center" style={{ gap: effectiveIsMobile ? '8px' : `${scaleW(16, scale.w)}px` }}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleSetActiveTab(tab.id)}
                  className="font-semibold transition-all duration-300"
                  style={{
                    color: activeTab === tab.id ? localTheme.textColor : localTheme.textColor + '99',
                    borderBottom: activeTab === tab.id ? `${scaleH(2, scale.h)}px solid ${localTheme.accentColor}` : `${scaleH(2, scale.h)}px solid transparent`,
                    paddingBottom: `${scaleH(4, scale.h)}px`,
                    fontSize: effectiveIsMobile ? '14px' : `${scaleW(18, scale.w)}px`
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.color = localTheme.textColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.color = localTheme.textColor + '99';
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Sort Dropdown */}
            <div className="relative" style={{ zIndex: 41, marginLeft: effectiveIsMobile ? 'auto' : `${scaleW(64, scale.w)}px` }}>
              <button
                onClick={handleToggleSortMenu}
                className="flex items-center rounded-lg font-medium transition-all"
                style={{
                  gap: `${scaleW(8, scale.w)}px`,
                  paddingLeft: `${scaleW(16, scale.w)}px`,
                  paddingRight: `${scaleW(16, scale.w)}px`,
                  background: themeColors.dropdownButtonBg,
                  border: 'none',
                  color: localTheme.textColor,
                  fontSize: `${scaleW(14, scale.w)}px`
                }}
              >
                <currentSortOption.icon size={scaleW(16, scale.w)} />
                <span>{currentSortOption.label}</span>
                <ChevronDown size={scaleW(16, scale.w)} className={showSortMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>

              {showSortMenu && (
                <>
                  <div
                    className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl"
                    style={{
                      backgroundColor: themeColors.dropdownMenuBg,
                      borderColor: localTheme.accentColor,
                      border: '2px solid',
                      zIndex: 42,
                      marginTop: `${scaleH(8, scale.h)}px`,
                      minWidth: `${scaleW(160, scale.w)}px`
                    }}
                  >
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className="flex items-center w-full text-left font-medium transition-all"
                        style={{
                          color: localTheme.textColor,
                          backgroundColor: option.value === sortBy
                            ? (customization.highlightColor || localTheme.accentColor)
                            : 'transparent',
                          gap: `${scaleW(8, scale.w)}px`,
                          paddingLeft: `${scaleW(16, scale.w)}px`,
                          paddingRight: `${scaleW(16, scale.w)}px`,
                          paddingTop: `${scaleH(12, scale.h)}px`,
                          paddingBottom: `${scaleH(12, scale.h)}px`,
                          fontSize: `${scaleW(14, scale.w)}px`
                        }}
                        onMouseEnter={(e) => {
                          if (option.value !== sortBy) {
                            const highlightColor = customization.highlightColor || localTheme.accentColor;
                            e.currentTarget.style.backgroundColor = highlightColor + '40';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (option.value !== sortBy) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <option.icon size={Math.round(scaleW(16, scale.w))} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 40 }}
                    onClick={handleCloseSortMenu}
                    onTouchStart={handleCloseSortMenu}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Wavy Divider */}
        <div className="w-full" style={{ marginTop: effectiveIsMobile ? '12px' : `${scaleH(32, scale.h)}px`, marginBottom: effectiveIsMobile ? '16px' : `${scaleH(48, scale.h)}px` }}>
          <svg width="100%" height={scaleH(20, scale.h)} viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
            <path
              d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10"
              fill="none"
              stroke={localTheme.accentColor}
              strokeWidth={scaleW(2, scale.w)}
              opacity="0.4"
            />
          </svg>
        </div>

        {/* Posts Display */}
        {(() => {
          // Show soft loading indicator when refreshing
          if (isRefreshing) {
            return <SoftLoad message="Refreshing..." />;
          }

          // Show login prompt for non-All tabs when not authenticated
          if (!isAuthenticated && activeTab !== 'all') {
            return (
              <div className="rounded-lg backdrop-blur-sm border text-center" style={{
                background: getCardBackground(localTheme, customization, theme.cardBg),
                borderColor: `${localTheme.accentColor}40`,
                marginBottom: '2rem',
                padding: `${scaleH(42, scale.h)}px ${scaleW(24, scale.w)}px`,
              }}>
                <div className="mb-6" style={{ color: localTheme.textColor, fontSize: `${scaleW(24, scale.w)}px`, fontWeight: 'bold' }}>
                  Login to View More
                </div>
                <div style={{ color: localTheme.textColor, opacity: 0.7, fontSize: `${scaleW(16, scale.w)}px`, lineHeight: '1.6' }}>
                  Sign in to see posts from your {activeTab === 'following' ? 'followed users' : activeTab === 'friends' ? 'friends' : activeTab === 'community' ? 'communities' : 'followed tags'}
                </div>
              </div>
            );
          }

          // Filter posts based on active tab
          let filteredPosts = posts;
          switch(activeTab) {
            case 'following':
              // Show posts from followed users
              filteredPosts = followingPosts;
              break;
            case 'friends':
              // Show posts from mutual followers
              filteredPosts = friendsPosts;
              break;
            case 'community':
              // Show community posts from joined communities and followed users
              filteredPosts = communityPosts;
              break;
            case 'tags':
              // Show posts with followed tags
              filteredPosts = tagsPosts;
              break;
            default: // 'all'
              filteredPosts = posts;
          }

          // Sort posts based on sortBy
          let sortedPosts = [...filteredPosts];
          switch(sortBy) {
            case 'newest':
              sortedPosts = sortedPosts.sort((a, b) =>
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );
              break;
            case 'oldest':
              sortedPosts = sortedPosts.sort((a, b) =>
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
              );
              break;
            case 'popular':
              sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
              break;
            case 'unpopular':
              sortedPosts = sortedPosts.sort((a, b) => (a.dislikes || 0) - (b.dislikes || 0));
              break;
            case 'controversial':
              sortedPosts = sortedPosts.sort((a, b) => {
                const aControversy = Math.min(a.likes, a.dislikes || 0);
                const bControversy = Math.min(b.likes, b.dislikes || 0);
                return bControversy - aControversy;
              });
              break;
            default:
              break;
          }

          return sortedPosts.length === 0 ? (
          <div className="text-center py-16">
            <h2 className="text-2xl font-bold mb-3" style={{ color: localTheme.textColor }}>No posts yet</h2>
            <p className="text-base" style={{ color: localTheme.textColor + '99' }}>Be the first to share something with the community!</p>
          </div>
        ) : (
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {sortedPosts.map((post, index) => {
              const postStyle = getPostStyle(index);
              return (
              <div
                key={post.id}
                ref={(el) => {
                  if (el) postRefs.current.set(post.id, el);
                  else postRefs.current.delete(post.id);
                }}
                data-post-id={post.id}
                className="rounded-lg p-5 backdrop-blur-sm border relative"
                style={{
                  background: getCardBackground(localTheme, customization, theme.cardBg),
                  borderColor: `${localTheme.accentColor}40`,
                  marginBottom: '2rem',
                  ...postStyle
                }}>
                {/* Post Badges */}
                <PostBadges post={post} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                {/* Moderate Dropdown */}
                {currentUser && canModeratePost(post, {
                  id: parseInt(currentUser.id),
                  username: currentUser.username,
                  isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
                  isModerator: (currentUser as any).isSiteModerator || false
                }) && (
                <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                  <button
                    onClick={() => handleToggleModerateMenu(post.id)}
                    className="p-1.5 rounded-lg transition-all hover:opacity-80"
                    style={{
                      backgroundColor: localTheme.accentColor + '20',
                      color: localTheme.textColor
                    }}
                  >
                    <MoreVertical size={16} />
                  </button>

                  {showModerateMenu === post.id && (
                    <>
                      <div
                        className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                        style={{
                          backgroundColor: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                          borderColor: localTheme.accentColor + '60',
                          border: '1px solid',
                          zIndex: 1001
                        }}
                      >
                        <button
                          onClick={() => handleEditPost(post)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Edit3 size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handlePinPost(post)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Pin size={16} />
                          {post.isPinned ? 'Unpin' : 'Pin'}
                        </button>
                        {(currentUser as any).isSuperAdmin && (
                          <button
                            onClick={() => handlePinToFeed(post)}
                            className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                            style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Pin size={16} />
                            {(post as any).isPinnedToFeed ? 'Unpin from Feed' : 'Pin to Feed'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePost(post)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: '#ef4444',
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef444420';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Trash size={16} />
                          Delete
                        </button>
                      </div>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 40 }}
                        onClick={handleCloseModerateMenu}
                        onTouchStart={handleCloseModerateMenu}
                      />
                    </>
                  )}
                </div>
                )}

                <div className="flex items-start gap-3 mb-3">
                  <Link to={post.authorUsername ? `/user/${post.authorUsername}` : '#'} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80" style={{
                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                  }}>
                    {post.authorAvatar ? (
                      <img src={post.authorAvatar} alt={post.author} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <PixelatedTurtle size={20} color={localTheme.accentColor} />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-0.5 mb-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold truncate" style={{ color: localTheme.textColor }}>{post.author}</h3>
                        {post.communityName && (
                          <span className="text-xs flex-shrink-0" style={{ color: localTheme.textColor + '99' }}>in {post.communityName}</span>
                        )}
                        <span className="text-xs flex-shrink-0" style={{ color: localTheme.textColor + '99' }}>{post.timestamp}</span>
                      </div>
                      {post.authorUsername && (
                        <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>@{post.authorUsername}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Moderation Disclaimer */}
                {post.editedByModeratorId && (
                  <div className="mb-3 px-3 py-2 rounded border" style={{
                    backgroundColor: '#854d0e20',
                    borderColor: '#ca8a0480'
                  }}>
                    <div className="flex items-center gap-2">
                      <AlertCircle size={14} className="text-yellow-400" />
                      <p className="text-xs text-yellow-100">
                        This post was edited by a moderator
                        {post.editedByModeratorAt && ` on ${formatDate(post.editedByModeratorAt)}`}
                      </p>
                    </div>
                    {post.moderatorEditReason && (
                      <p className="text-xs text-yellow-100/80 mt-1">
                        Reason: {post.moderatorEditReason}
                      </p>
                    )}
                  </div>
                )}

                {/* Post Title and Content */}
                <Link
                  to={getPostUrl(post)}
                  className="block hover:opacity-80 transition-opacity cursor-pointer"
                >
                    {post.title && (
                      <h2 className="text-base font-bold mb-2" style={{ color: localTheme.textColor }}>
                        {post.title}
                      </h2>
                    )}
                    {/* Media above content */}
                    {post.aboveContent && post.mediaUrl && (
                      <div className="mb-3 rounded-lg overflow-hidden">
                        <img
                          src={post.mediaUrl}
                          alt="Post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                          onClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    <p className="text-sm leading-relaxed mb-3 whitespace-pre-wrap" style={{ color: localTheme.textColor }}>
                      {post.content}
                    </p>
                    {/* Media below content */}
                    {post.belowContent && post.mediaUrl && (
                      <div className="mt-3 rounded-lg overflow-hidden">
                        <img
                          src={post.mediaUrl}
                          alt="Post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                          onClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </Link>

                {/* Post Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity"
                        style={{
                          backgroundColor: localTheme.accentColor + '30',
                          color: localTheme.textColor
                        }}
                      >
                        <Hash size={10} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => likePost(post.id)}
                      className="flex items-center gap-1.5 transition-colors"
                      style={{
                        color: post.liked ? "#f87171" : localTheme.textColor + '99'
                      }}
                    >
                      <Heart
                        size={16}
                        className={post.liked ? "fill-current" : ""}
                      />
                      <span className="text-xs">{post.likes}</span>
                    </button>

                    <button
                      onClick={() => dislikePost(post.id)}
                      className="flex items-center gap-1.5 transition-colors"
                      style={{
                        color: post.disliked ? "#f97316" : localTheme.textColor + '99'
                      }}
                    >
                      <ThumbsDown
                        size={16}
                        className={post.disliked ? "fill-current" : ""}
                      />
                      <span className="text-xs">{post.dislikes}</span>
                    </button>

                    <button
                      onClick={() => navigate(getPostUrl(post))}
                      className="flex items-center gap-1.5 transition-colors hover:text-purple-400"
                      style={{ color: localTheme.textColor + '99' }}
                    >
                      <MessageSquare size={16} />
                      <span className="text-xs">{post.comments}</span>
                    </button>

                    <button
                      onClick={() => handleRepost(post.id)}
                      className="flex items-center gap-1.5 transition-colors hover:text-green-400"
                      style={{ color: localTheme.textColor + '99' }}
                    >
                      <Repeat2 size={16} />
                      <span className="text-xs">{post.shares}</span>
                    </button>

                    <button
                      onClick={() => handleShare(post.id)}
                      className="flex items-center gap-1.5 transition-colors hover:text-blue-400"
                      style={{ color: localTheme.textColor + '99' }}
                    >
                      <Share2 size={16} />
                    </button>
                  </div>

                  <button className="transition-colors" style={{ color: localTheme.textColor + '99' }}>
                    <Bookmark size={16} />
                  </button>
                </div>

                {/* Comment Preview - Max 3 comments */}
                {(() => {
                  if (!post.replies || post.replies.length === 0) return null;
                  const previewComments = selectPreviewComments(post.replies);
                  return (
                    <div className="mt-4 pt-4 " style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                      <div className="space-y-3">
                        {previewComments.map((reply) => (
                          <div key={reply.id} className="flex gap-3">
                            <Link
                              to={`/user/${reply.author.username}`}
                              className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-opacity hover:opacity-80"
                              style={{
                                background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                              }}
                            >
                              {reply.author.profilePicture ? (
                                <img src={resolveAvatar(reply.author.profilePicture)} alt={reply.author.profileName || reply.author.username} className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <PixelatedTurtle size={16} color={localTheme.accentColor} />
                              )}
                            </Link>
                            <div className="flex-1 relative">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold" style={{ color: localTheme.textColor }}>
                                  {reply.author.profileName || reply.author.username}
                                </span>
                                <span className="text-xs" style={{ color: localTheme.textColor + '66' }}>
                                  {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                                <div className="ml-auto relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCommentMenuId(commentMenuId === reply.id ? null : reply.id);
                                    }}
                                    className="p-0.5 rounded hover:bg-white/10 transition-colors"
                                    style={{ color: localTheme.textColor + '44' }}
                                  >
                                    <MoreVertical size={14} />
                                  </button>
                                  {commentMenuId === reply.id && (
                                    <div
                                      className="absolute right-0 top-6 z-50 rounded-lg py-1 shadow-lg"
                                      style={{
                                        background: 'var(--navbar-bg, #1a1a2e)',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        minWidth: 120,
                                      }}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCommentMenuId(null);
                                          setReportingCommentId(reply.id);
                                        }}
                                        className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-white/10 transition-colors"
                                        style={{ color: '#ef4444' }}
                                      >
                                        <Flag size={12} />
                                        Report
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs" style={{ color: localTheme.textColor + 'cc' }}>{reply.content}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    likeComment(reply.id, post.id);
                                  }}
                                  className="flex items-center gap-1 transition-colors hover:opacity-80"
                                  style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '66' }}
                                >
                                  <Heart size={12} fill={reply.liked ? '#ef4444' : 'none'} />
                                  <span className="text-xs">{reply.likes}</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dislikeComment(reply.id, post.id);
                                  }}
                                  className="flex items-center gap-1 transition-colors hover:opacity-80"
                                  style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '66' }}
                                >
                                  <ThumbsDown size={12} fill={reply.disliked ? '#f97316' : 'none'} />
                                  <span className="text-xs">{reply.dislikes}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {post.comments > 3 && (
                        <div className="mt-2">
                          <button
                            className="text-xs transition-opacity hover:opacity-70"
                            style={{ color: localTheme.accentColor }}
                            onClick={() => navigate(getPostUrl(post))}
                          >
                            View all {post.comments} comments
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              );
            })}
          </div>
        );
        })()}
    </>
  );

  const renderRightColumn = () => (
    <>
        {/* Spacers - skip on mobile where MobileShell provides header */}
        {!effectiveIsMobile && (
          <>
            <div className="pb-4"></div>
            <div className="w-full py-5 mb-6"></div>
            <div className="w-full mt-8 mb-12" style={{ height: '20px' }}></div>
          </>
        )}
        <div className={effectiveIsMobile ? "flex flex-col gap-4" : "flex gap-6"}>
        {/* Navigate Widget */}
        <div style={{ width: effectiveIsMobile ? '100%' : `${scaleW(320, scale.w)}px` }}>
          <div className="rounded-lg backdrop-blur-sm border sticky" style={{
            top: `${scaleH(120, scale.h)}px`,
            background: getCardBackground(localTheme, customization, theme.cardBg),
            borderColor: `${localTheme.accentColor}40`,
            zIndex: 10,
            padding: 0
          }}>
            <h3 className="font-bold text-center border-b" style={{
              color: localTheme.textColor,
              borderColor: `${localTheme.accentColor}40`,
              fontSize: `${scaleW(18, scale.w)}px`,
              paddingTop: `${scaleH(12, scale.h)}px`,
              paddingBottom: `${scaleH(12, scale.h)}px`
            }}>Navigate</h3>

            <div style={{ padding: `${scaleH(12, scale.h)}px` }}>
              {/* Navigate Input */}
              <input
                type="text"
                placeholder="Navigate..."
                value={sidebarSearchQuery}
                onChange={(e) => setSidebarSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && sidebarSearchQuery.trim()) {
                    setShowSidebarSearchResults(true);
                  }
                }}
                onFocus={() => {
                  if (sidebarSearchQuery.trim()) {
                    setShowSidebarSearchResults(true);
                  }
                }}
                className="w-full rounded-lg outline-none transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`,
                  padding: `${scaleH(12, scale.h)}px ${scaleW(12, scale.w)}px`,
                  height: `${scaleH(40, scale.h)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`,
                  marginBottom: `${scaleH(12, scale.h)}px`
                }}
              />

              {/* Search Type Selector */}
              <div className="flex gap-0 rounded-lg overflow-hidden" style={{
                border: `1px solid ${localTheme.accentColor}40`
              }}>
              <button
                onClick={() => handleSetSidebarSearchType('people')}
                className="flex-1 font-semibold transition-all"
                style={{
                  backgroundColor: sidebarSearchType === 'people' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: localTheme.textColor,
                  borderRight: `1px solid ${localTheme.accentColor}40`,
                  paddingTop: `${scaleH(8, scale.h)}px`,
                  paddingBottom: `${scaleH(8, scale.h)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`
                }}
              >
                People
              </button>
              <button
                onClick={() => handleSetSidebarSearchType('communities')}
                className="flex-1 font-semibold transition-all"
                style={{
                  backgroundColor: sidebarSearchType === 'communities' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: localTheme.textColor,
                  borderRight: `1px solid ${localTheme.accentColor}40`,
                  paddingTop: `${scaleH(8, scale.h)}px`,
                  paddingBottom: `${scaleH(8, scale.h)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`
                }}
              >
                Communities
              </button>
              <button
                onClick={() => handleSetSidebarSearchType('tags')}
                className="flex-1 font-semibold transition-all"
                style={{
                  backgroundColor: sidebarSearchType === 'tags' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: localTheme.textColor,
                  paddingTop: `${scaleH(8, scale.h)}px`,
                  paddingBottom: `${scaleH(8, scale.h)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`
                }}
              >
                Tags
              </button>
              </div>

              {/* Animated Search Results Dropdown */}
              {showSidebarSearchResults && sidebarSearchQuery.trim() && (
                <div
                  className="mt-3 rounded-lg border overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  background: getCardBackground(localTheme, customization, theme.cardBg),
                  borderColor: `${localTheme.accentColor}40`,
                  maxHeight: '400px',
                  animation: 'slideDown 0.3s ease-out'
                }}
              >
                  <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                    {/* People Results */}
                    {sidebarSearchType === 'people' && (
                      <div className="p-3 space-y-2">
                        {isSearching ? (
                          <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                            Searching...
                          </div>
                        ) : sidebarSearchPeople.length > 0 ? (
                          sidebarSearchPeople.map((person) => (
                            <div
                              key={person.id}
                              onClick={() => {
                                navigate(`/user/${person.username}`);
                                setShowSidebarSearchResults(false);
                                setSidebarSearchQuery('');
                              }}
                              className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                            >
                              {person.avatar ? (
                                <img
                                  src={person.avatar}
                                  alt={person.displayName}
                                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div
                                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  <PixelatedTurtle size={20} color={localTheme.accentColor} />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>{person.displayName}</div>
                                <div className="text-xs truncate" style={{ color: localTheme.textColor + '99' }}>@{person.username}</div>
                              </div>
                              <div className="text-xs whitespace-nowrap" style={{ color: localTheme.textColor + '66' }}>
                                {person.followers} followers
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                            No people found
                          </div>
                        )}
                      </div>
                    )}

                    {/* Communities Results */}
                    {sidebarSearchType === 'communities' && (
                      <div className="p-3 space-y-2">
                        {isSearching ? (
                          <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                            Searching...
                          </div>
                        ) : sidebarSearchCommunities.length > 0 ? (
                          sidebarSearchCommunities.map((comm) => (
                            <div
                              key={comm.id}
                              onClick={() => {
                                navigate(`/community/${comm.nameSlug || comm.name.toLowerCase().replace(/\s+/g, '-')}`);
                                setShowSidebarSearchResults(false);
                                setSidebarSearchQuery('');
                              }}
                              className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                            >
                              {comm.avatar ? (
                                <img
                                  src={comm.avatar}
                                  alt={comm.name}
                                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div
                                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  <PixelatedCoral size={20} />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>{comm.name}</div>
                                <div className="text-xs truncate" style={{ color: localTheme.textColor + '99' }}>{comm.members} members</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                            No communities found
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tags Results */}
                    {sidebarSearchType === 'tags' && (
                      <div className="p-3 space-y-2">
                        {isSearching ? (
                          <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                            Searching...
                          </div>
                        ) : sidebarSearchTags.length > 0 ? (
                          sidebarSearchTags.map((tagData) => (
                            <div
                              key={tagData.tag}
                              className="flex items-center gap-3 p-2 rounded-lg transition-all"
                              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                            >
                              <div
                                onClick={() => {
                                  navigate(`/tags/${tagData.tag}`);
                                  setShowSidebarSearchResults(false);
                                  setSidebarSearchQuery('');
                                }}
                                className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80"
                              >
                                <div
                                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{
                                    backgroundColor: localTheme.accentColor + '30',
                                    border: `1px solid ${localTheme.accentColor}`
                                  }}
                                >
                                  <Hash size={20} style={{ color: localTheme.textColor }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm truncate" style={{ color: localTheme.textColor }}>#{tagData.tag}</div>
                                  <div className="text-xs truncate" style={{ color: localTheme.textColor + '99' }}>{tagData.count} posts</div>
                                </div>
                              </div>
                              <button
                                onClick={(e) => handleToggleFollowTag(tagData.tag, e)}
                                className="px-3 py-1 rounded text-xs font-semibold transition-all hover:opacity-80 flex-shrink-0"
                                style={{
                                  backgroundColor: followedTags.includes(tagData.tag) ? 'rgba(255, 255, 255, 0.1)' : localTheme.accentColor,
                                  color: localTheme.textColor,
                                  border: followedTags.includes(tagData.tag) ? `1px solid ${localTheme.accentColor}` : 'none'
                                }}
                              >
                                {followedTags.includes(tagData.tag) ? 'Following' : 'Follow'}
                              </button>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4" style={{ color: localTheme.textColor + '99' }}>
                            No tags found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

          {/* Right Column - Stacked Widgets */}
          <div style={{ width: effectiveIsMobile ? '100%' : `${scaleW(320, scale.w)}px` }}>
            {/* Friends */}
            <div className="rounded-lg backdrop-blur-sm border" style={{
              background: getCardBackground(localTheme, customization, theme.cardBg),
              borderColor: `${localTheme.accentColor}40`,
              marginBottom: `${scaleH(32, scale.h)}px`,
              padding: 0,
              position: 'relative',
              zIndex: 10
            }}>
              <h3 className="font-bold text-center" style={{ color: localTheme.textColor, borderBottom: `${localTheme.accentColor}40`, paddingLeft: `${scaleW(20, scale.w)}px`, paddingRight: `${scaleW(20, scale.w)}px`, paddingTop: `${scaleH(12, scale.h)}px`, paddingBottom: `${scaleH(12, scale.h)}px`, fontSize: `${scaleW(18, scale.w)}px` }}>Friends</h3>
              <div style={{ padding: `${scaleH(20, scale.h)}px ${scaleW(20, scale.w)}px` }}>
                {friendsUsers.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(12, scale.h)}px`, marginBottom: `${scaleH(16, scale.h)}px` }}>
                      {friendsUsers.slice(0, 20).map((user) => (
                        <Link
                          key={user.id}
                          to={`/user/${user.username}`}
                          className="flex items-center gap-3 rounded-lg transition-all cursor-pointer hover:opacity-80"
                          style={{
                            backgroundColor: localTheme.accentColor + '10',
                            minWidth: effectiveIsMobile ? 0 : 'max-content',
                            paddingTop: `${scaleH(8, scale.h)}px`,
                            paddingBottom: `${scaleH(8, scale.h)}px`
                          }}
                        >
                          <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{
                            backgroundColor: localTheme.accentColor + '40',
                            width: `${scaleW(40, scale.w)}px`,
                            height: `${scaleW(40, scale.w)}px`
                          }}>
                            <span style={{ color: localTheme.textColor, fontSize: `${scaleW(16, scale.w)}px` }}>{user.displayName?.[0] || user.username[0]}</span>
                          </div>
                          <div className="flex-1" style={{ minWidth: 'max-content' }}>
                            <p className="font-semibold" style={{ color: localTheme.textColor, whiteSpace: effectiveIsMobile ? 'normal' : 'nowrap', fontSize: `${scaleW(14, scale.w)}px` }}>{user.displayName || user.username}</p>
                            <p style={{ color: localTheme.textColor + '99', whiteSpace: effectiveIsMobile ? 'normal' : 'nowrap', fontSize: `${scaleW(12, scale.w)}px` }}>@{user.username}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {friendsUsers.length > 20 && (
                      <button
                        onClick={() => navigate('/friends')}
                        className="w-full rounded-lg font-semibold transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor,
                          paddingTop: `${scaleH(8, scale.h)}px`,
                          paddingBottom: `${scaleH(8, scale.h)}px`,
                          fontSize: `${scaleW(14, scale.w)}px`
                        }}
                      >
                        See All Friends
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center">
                    <Users size={scaleW(48, scale.w)} style={{ color: localTheme.textColor + '40', marginBottom: `${scaleH(12, scale.h)}px` }} />
                    <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(14, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                      No friends yet
                    </p>
                    <p style={{ color: localTheme.textColor + '66', fontSize: `${scaleW(12, scale.w)}px` }}>
                      Follow people back to become friends
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Following Widget */}
            <div className="rounded-lg backdrop-blur-sm border" style={{
              background: getCardBackground(localTheme, customization, theme.cardBg),
              borderColor: `${localTheme.accentColor}40`,
              marginBottom: `${scaleH(32, scale.h)}px`,
              padding: 0,
              position: 'relative',
              zIndex: 10
            }}>
              <h3 className="font-bold text-center" style={{ color: localTheme.textColor, borderBottom: `${localTheme.accentColor}40`, paddingLeft: `${scaleW(20, scale.w)}px`, paddingRight: `${scaleW(20, scale.w)}px`, paddingTop: `${scaleH(12, scale.h)}px`, paddingBottom: `${scaleH(12, scale.h)}px`, fontSize: `${scaleW(18, scale.w)}px` }}>Following</h3>
              <div style={{ padding: `${scaleH(20, scale.h)}px ${scaleW(20, scale.w)}px` }}>
                {followingUsers.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(12, scale.h)}px`, marginBottom: `${scaleH(16, scale.h)}px` }}>
                      {followingUsers.slice(0, 20).map((user) => (
                        <Link
                          key={user.id}
                          to={`/user/${user.username}`}
                          className="flex items-center gap-3 rounded-lg transition-all cursor-pointer hover:opacity-80"
                          style={{
                            backgroundColor: localTheme.accentColor + '10',
                            minWidth: effectiveIsMobile ? 0 : 'max-content',
                            paddingTop: `${scaleH(8, scale.h)}px`,
                            paddingBottom: `${scaleH(8, scale.h)}px`
                          }}
                        >
                          <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{
                            backgroundColor: localTheme.accentColor + '40',
                            width: `${scaleW(40, scale.w)}px`,
                            height: `${scaleW(40, scale.w)}px`
                          }}>
                            <span style={{ color: localTheme.textColor, fontSize: `${scaleW(16, scale.w)}px` }}>{user.displayName?.[0] || user.username[0]}</span>
                          </div>
                          <div className="flex-1" style={{ minWidth: 'max-content' }}>
                            <p className="font-semibold" style={{ color: localTheme.textColor, whiteSpace: effectiveIsMobile ? 'normal' : 'nowrap', fontSize: `${scaleW(14, scale.w)}px` }}>{user.displayName || user.username}</p>
                            <p style={{ color: localTheme.textColor + '99', whiteSpace: effectiveIsMobile ? 'normal' : 'nowrap', fontSize: `${scaleW(12, scale.w)}px` }}>@{user.username}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {followingUsers.length > 20 && (
                      <button
                        onClick={() => navigate('/friends')}
                        className="w-full rounded-lg font-semibold transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor,
                          border: `1px solid ${localTheme.accentColor}40`,
                          paddingTop: `${scaleH(8, scale.h)}px`,
                          paddingBottom: `${scaleH(8, scale.h)}px`,
                          fontSize: `${scaleW(14, scale.w)}px`
                        }}
                      >
                        Load More...
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center">
                    <Users size={scaleW(48, scale.w)} style={{ color: localTheme.textColor + '40', marginBottom: `${scaleH(12, scale.h)}px` }} />
                    <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(14, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                      Not following anyone yet
                    </p>
                    <p style={{ color: localTheme.textColor + '66', fontSize: `${scaleW(12, scale.w)}px` }}>
                      Find people to follow
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Followers Widget */}
            <div className="rounded-lg backdrop-blur-sm border" style={{
              background: getCardBackground(localTheme, customization, theme.cardBg),
              borderColor: `${localTheme.accentColor}40`,
              padding: 0,
              position: 'relative',
              zIndex: 10
            }}>
              <h3 className="font-bold text-center" style={{ color: localTheme.textColor, borderBottom: `${localTheme.accentColor}40`, paddingLeft: `${scaleW(20, scale.w)}px`, paddingRight: `${scaleW(20, scale.w)}px`, paddingTop: `${scaleH(12, scale.h)}px`, paddingBottom: `${scaleH(12, scale.h)}px`, fontSize: `${scaleW(18, scale.w)}px` }}>Followers</h3>
              <div style={{ padding: `${scaleH(20, scale.h)}px ${scaleW(20, scale.w)}px` }}>
                {followersUsers.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(12, scale.h)}px`, marginBottom: `${scaleH(16, scale.h)}px` }}>
                      {followersUsers.slice(0, 20).map((user) => {
                        const isFollowing = followingUsers.some(u => u.id === user.id);
                        return (
                          <div
                            key={user.id}
                            className="flex items-center gap-3 rounded-lg transition-all"
                            style={{
                              backgroundColor: localTheme.accentColor + '10',
                              minWidth: effectiveIsMobile ? 0 : 'max-content',
                              paddingTop: `${scaleH(8, scale.h)}px`,
                              paddingBottom: `${scaleH(8, scale.h)}px`
                            }}
                          >
                            <Link
                              to={`/user/${user.username}`}
                              className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80"
                              style={{ minWidth: 'max-content' }}
                            >
                              <div
                                className="rounded-full flex items-center justify-center flex-shrink-0"
                                style={{
                                  backgroundColor: localTheme.accentColor + '40',
                                  width: `${scaleW(40, scale.w)}px`,
                                  height: `${scaleW(40, scale.w)}px`
                                }}
                              >
                                <span style={{ color: localTheme.textColor, fontSize: `${scaleW(16, scale.w)}px` }}>{user.displayName?.[0] || user.username[0]}</span>
                              </div>
                              <div className="flex-1" style={{ minWidth: 'max-content' }}>
                                <p className="font-semibold" style={{ color: localTheme.textColor, whiteSpace: effectiveIsMobile ? 'normal' : 'nowrap', fontSize: `${scaleW(14, scale.w)}px` }}>{user.displayName || user.username}</p>
                                <p style={{ color: localTheme.textColor + '99', whiteSpace: effectiveIsMobile ? 'normal' : 'nowrap', fontSize: `${scaleW(12, scale.w)}px` }}>@{user.username}</p>
                              </div>
                            </Link>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const { followUser, unfollowUser } = await import('../../lib/api-client');
                                  if (isFollowing) {
                                    await unfollowUser(String(user.id));
                                  } else {
                                    await followUser(String(user.id));
                                  }
                                  window.dispatchEvent(new CustomEvent('followingUpdated'));
                                } catch (error) {
                                }
                              }}
                              className="font-semibold rounded-lg transition-all hover:opacity-80"
                              style={{
                                backgroundColor: isFollowing ? 'transparent' : localTheme.accentColor,
                                color: isFollowing ? localTheme.accentColor : localTheme.textColor,
                                border: `1px solid ${isFollowing ? localTheme.accentColor : 'transparent'}`,
                                minWidth: `${scaleW(64, scale.w)}px`,
                                paddingLeft: `${scaleW(12, scale.w)}px`,
                                paddingRight: `${scaleW(12, scale.w)}px`,
                                paddingTop: `${scaleH(4, scale.h)}px`,
                                paddingBottom: `${scaleH(4, scale.h)}px`,
                                fontSize: `${scaleW(12, scale.w)}px`
                              }}
                            >
                              {isFollowing ? 'Following' : 'Follow'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    {followersUsers.length > 20 && (
                      <button
                        onClick={() => navigate('/friends')}
                        className="w-full rounded-lg font-semibold transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor,
                          border: `1px solid ${localTheme.accentColor}40`,
                          paddingTop: `${scaleH(8, scale.h)}px`,
                          paddingBottom: `${scaleH(8, scale.h)}px`,
                          fontSize: `${scaleW(14, scale.w)}px`
                        }}
                      >
                        Load More...
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center">
                    <Users size={scaleW(48, scale.w)} style={{ color: localTheme.textColor + '40', marginBottom: `${scaleH(12, scale.h)}px` }} />
                    <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(14, scale.w)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                      No followers yet
                    </p>
                    <p style={{ color: localTheme.textColor + '66', fontSize: `${scaleW(12, scale.w)}px` }}>
                      Share your profile to gain followers
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </>
  );

  const renderModals = () => {
    const modals = (
    <>
        {/* Post Creation Modal */}
        <PostCreationModal
          isOpen={showPostWidget}
          onClose={() => setShowPostWidget(false)}
          onSubmit={handleCreatePost}
          currentUser={currentUser}
          theme={localTheme}
          scale={scale}
          scaleW={scaleW}
          scaleH={scaleH}
          getCardBackground={getCardBackground}
          customization={customization}
        />

        {/* Search Modal */}
        {showSearchModal && (
          <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-20 bg-black/60">
            <div className="w-full max-w-2xl rounded-lg bg-[#1e3a5f] border border-[#2d4a6f]">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-[#2d4a6f]">
                <h2 className="text-lg font-bold text-white">Search</h2>
                <button
                  onClick={() => {
                    setShowSearchModal(false);
                    setSearchQuery("");
                  }}
                  className="text-white/60 hover:text-white transition-colors text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Search Type Tabs */}
              <div className="flex border-b border-[#2d4a6f]">
                <button
                  onClick={() => setSearchType("profiles")}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    searchType === "profiles"
                      ? "text-white bg-[#0c4a6e]/40 border-b-2 border-[#0891b2]"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  Profiles
                </button>
                <button
                  onClick={() => setSearchType("communities")}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    searchType === "communities"
                      ? "text-white bg-[#0c4a6e]/40 border-b-2 border-[#0891b2]"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  Communities
                </button>
              </div>

              {/* Search Input */}
              <div className="p-4">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search ${searchType}...`}
                    className="w-full pl-10 pr-4 py-3 bg-[#0c4a6e]/40 border border-[#2d4a6f] rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#0891b2] focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>

              {/* Search Results */}
              <div className="p-4 max-h-96 overflow-y-auto">
                {searchQuery.trim() === "" ? (
                  <div className="text-center py-8 text-white/60 text-sm">
                    {searchType === "profiles" ? "Search for people by name or username" : "Search for communities by name"}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Mock results - replace with actual search logic */}
                    {[1, 2, 3].map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-3 p-3 rounded-lg bg-[#0c4a6e]/40 hover:bg-[#0c4a6e]/60 cursor-pointer transition-colors"
                        onClick={() => {
                          setShowSearchModal(false);
                          setSearchQuery("");
                          // Navigate to profile/community
                        }}
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0891b2] to-[#06b6d4] flex items-center justify-center flex-shrink-0">
                          {searchType === "profiles" ? (
                            <PixelatedTurtle size={20} color={localTheme.accentColor} />
                          ) : (
                            <Users size={20} className="text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-white">
                            {searchType === "profiles" ? "Sample User" : "Sample Community"}
                          </div>
                          <div className="text-xs text-white/60">
                            {searchType === "profiles" ? "@sampleuser" : "123 members"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Refresh Button - hidden on mobile (swipe-to-refresh exists) */}
        {!effectiveIsMobile && (
          <button
            onClick={handleRefresh}
            className="group fixed z-40 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:shadow-xl"
            style={{
              top: `${scaleH(100, scale.h)}px`,
              right: `${scaleW(112, scale.w)}px`,
              backgroundColor: localTheme.accentColor,
              color: localTheme.textColor,
              width: `${scaleW(56, scale.w)}px`,
              height: `${scaleH(56, scale.h)}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw
              size={Math.round(scaleW(24, scale.w))}
              className={`transition-transform ${isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'}`}
            />
            <span
              className="absolute right-full mr-3 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none"
              style={{
                backgroundColor: localTheme.accentColor,
                color: localTheme.textColor,
              }}
            >
              Refresh
            </span>
          </button>
        )}

        {/* Standardized Theme Customizer Button - hidden on mobile */}
        {!effectiveIsMobile && (
          <ThemeCustomizerButton
            onClick={() => setShowSiteCustomizer(!showSiteCustomizer)}
            tooltipText="Customize Site Theme"
            accentColor={localTheme.accentColor}
            textColor={localTheme.textColor}
            show={true}
          />
        )}

        {/* Site Theme Customizer Modal */}
        {currentUser && (
          <SiteThemeCustomizer
            isOpen={showSiteCustomizer}
            onClose={() => { requestAnimationFrame(() => setShowSiteCustomizer(false)); }}
            userId={currentUser.id}
            initialCustomization={customization}
            initialAnimations={themeAnimations}
            initialAnimationsEnabled={animationsEnabled}
          />
        )}

        {/* Repost Modal */}
        {showRepostModal && (
          <div
            ref={repostModalRef}
            className="fixed z-[100] max-w-md w-full mx-4"
            style={{
              left: '50%',
              top: '5rem',
              transform: `translate(calc(-50% + ${repostModalPosition.x}px), ${repostModalPosition.y}px)`,
              cursor: isDraggingRepost ? 'grabbing' : 'default'
            }}
            onMouseMove={(e) => {
              if (isDraggingRepost) {
                const deltaX = e.clientX - dragStartRepost.x;
                const deltaY = e.clientY - dragStartRepost.y;
                setRepostModalPosition({ x: repostModalPosition.x + deltaX, y: repostModalPosition.y + deltaY });
                setDragStartRepost({ x: e.clientX, y: e.clientY });
              }
            }}
            onMouseUp={() => setIsDraggingRepost(false)}
            onMouseLeave={() => setIsDraggingRepost(false)}
          >
            <div className="rounded-lg backdrop-blur-sm border shadow-2xl" style={{
              background: getCardBackground(localTheme, customization),
              borderColor: `${localTheme.accentColor}40`
            }}>
              <div
                className="flex items-center justify-between mb-4 p-6 pb-2 cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => {
                  setIsDraggingRepost(true);
                  setDragStartRepost({ x: e.clientX, y: e.clientY });
                }}
                style={{
                  borderBottom: `1px solid ${localTheme.accentColor}20`,
                  userSelect: 'none'
                }}
              >
                <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Repost</h3>
                <button
                  onClick={() => {
                    setShowRepostModal(false);
                    setRepostModalPosition({ x: 0, y: 0 });
                  }}
                  className="transition-colors hover:opacity-70"
                  style={{ color: localTheme.textColor }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <X size={24} />
                </button>
              </div>
              <div className="px-6 pb-6">
              <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Add an optional comment to your repost:</p>
              <textarea
                value={repostComment}
                onChange={(e) => setRepostComment(e.target.value)}
                placeholder="What do you think about this?"
                className="w-full p-3 rounded-lg outline-none resize-none transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
                rows={4}
                maxLength={280}
              />
              <div className="text-right text-sm mb-4" style={{ color: localTheme.textColor + '99' }}>{repostComment.length}/280</div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRepostModal(false);
                    setRepostComment('');
                  }}
                  className="flex-1 py-2 rounded-lg transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: localTheme.textColor,
                    border: `1px solid ${localTheme.accentColor}40`
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitRepost}
                  disabled={isReposting}
                  className="flex-1 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor
                  }}
                >
                  {isReposting ? 'Reposting...' : 'Repost'}
                </button>
              </div>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <>
          <div
            className="fixed inset-0"
            style={{ zIndex: 99, background: effectiveIsMobile ? 'rgba(0,0,0,0.5)' : 'transparent' }}
            onClick={() => { setShowShareModal(false); setSharePostId(null); }}
            onTouchStart={() => { setShowShareModal(false); setSharePostId(null); }}
          />
          <div className="fixed left-1/2 transform -translate-x-1/2 z-[100]" style={{
            top: effectiveIsMobile ? '30%' : `${scaleH(80, scale.h)}px`,
            width: effectiveIsMobile ? 'calc(100% - 32px)' : undefined,
            maxWidth: effectiveIsMobile ? '400px' : `${scaleW(448, scale.w)}px`,
          }}>
            <div className="rounded-lg backdrop-blur-sm border shadow-2xl" style={{
              background: getCardBackground(localTheme, customization),
              borderColor: `${localTheme.accentColor}40`,
              padding: `${scaleH(24, scale.h)}px`
            }}>
              <div className="flex items-center justify-between" style={{ marginBottom: `${scaleH(16, scale.h)}px` }}>
                <h3 className="font-bold" style={{
                  color: localTheme.textColor,
                  fontSize: `${scaleW(20, scale.w)}px`
                }}>Share Post</h3>
                <button onClick={() => setShowShareModal(false)} className="transition-colors hover:opacity-70" style={{ color: localTheme.textColor }}>
                  <X size={Math.round(scaleW(24, scale.w))} />
                </button>
              </div>
              <p style={{
                color: localTheme.textColor + 'cc',
                marginBottom: `${scaleH(16, scale.h)}px`,
                fontSize: `${scaleW(14, scale.w)}px`
              }}>Share this post:</p>
              <button
                onClick={handleCopyLink}
                className="w-full rounded-lg transition-all flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`,
                  paddingTop: `${scaleH(12, scale.h)}px`,
                  paddingBottom: `${scaleH(12, scale.h)}px`,
                  gap: `${scaleW(8, scale.w)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`
                }}
              >
                <Link2 size={Math.round(scaleW(18, scale.w))} />
                Copy Link
              </button>
            </div>
          </div>
          </>
        )}

        {/* Edit Post Modal */}
        {editingPost && (
          <EditPostModal
            post={editingPost}
            isOpen={!!editingPost}
            onClose={() => setEditingPost(null)}
            onSave={handleSaveEdit}
          />
        )}
        <ReportModal
          isOpen={!!reportingCommentId}
          onClose={() => setReportingCommentId(null)}
          commentId={reportingCommentId || undefined}
        />
    </>
  );
    return effectiveIsMobile ? createPortal(modals, document.body) : modals;
  };

  // ─── Mobile Layout ───
  if (mobileMode) {
    return (
      <div style={{ background: getBackground(localTheme, customization, theme.appBg), height: '100%', overflow: 'hidden' }}>
        <SubPanelSwiper
          activePanel={{ left: 0, middle: 1, right: 2 }[currentSection]}
          onPanelChange={(i: number) => {
            const section = (['left', 'middle', 'right'] as const)[i];
            setCurrentSection(section);
          }}
          screenWidth={mobileScreenWidth}
        >
          {[
            <div key="left" className="p-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top) + 1rem)' }}>
              {leftColumnContent}
            </div>,
            <div key="center" ref={pullToRefresh.containerRef} className="flex flex-col items-center w-full px-2 h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Pull-to-refresh indicator */}
              {(pullToRefresh.isPulling || pullToRefresh.isRefreshing) && (
                <div className="flex items-center justify-center shrink-0" style={{
                  height: `${pullToRefresh.pullDistance}px`,
                  transition: pullToRefresh.isPulling ? 'none' : 'height 0.3s ease-out',
                  overflow: 'hidden',
                }}>
                  <div className={`w-6 h-6 rounded-full border-2 border-t-transparent ${pullToRefresh.isRefreshing ? 'animate-spin' : ''}`}
                    style={{
                      borderColor: 'var(--color-accent, #06b6d4)',
                      borderTopColor: 'transparent',
                      opacity: Math.min(pullToRefresh.pullDistance / 60, 1),
                    }}
                  />
                </div>
              )}
              <div style={pullToRefresh.pullIndicatorStyle} className="w-full flex flex-col items-center">
                {renderCenterColumn()}
              </div>
            </div>,
            <div key="right" className="pt-6 px-4 flex flex-col gap-4">
              {/* Customize Theme button on right/widgets panel */}
              {currentUser && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => setShowSiteCustomizer(true));
                  }}
                  className="rounded-lg font-semibold w-full flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor,
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    minHeight: '48px',
                  }}
                >
                  Customize Theme
                </button>
              )}
              {renderRightColumn()}
            </div>,
          ]}
        </SubPanelSwiper>
        {renderModals()}
      </div>
    );
  }

  return (
    <div style={{ background: getBackground(localTheme, customization, theme.appBg), minHeight: backgroundHeight }}>
      <style>{`
        .pixelated {
          image-rendering: pixelated;
          image-rendering: -moz-crisp-edges;
          image-rendering: crisp-edges;
        }
      `}</style>

      {/* Theme-Specific Floating Elements */}
      <AnimationLayer
        animations={themeAnimations}
        isMobile={false}
        bubbleColor={localTheme.accentColor}
        enabled={animationsEnabled}
      />

      {/* Top Navigation Bar */}
      <MainNavbar
        currentPath="/feed"
        onLogout={handleLogout}
        onSignIn={() => setShowLoginModal(true)}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        theme={localTheme}
        customization={customization}
        navbarBg={localTheme.navbarBg}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        getNotificationIcon={getNotificationIcon}
        getNotificationMessage={getNotificationMessage}
        formatNotificationTime={formatNotificationTime}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
      />

      {/* Page Header */}
      <div style={{ paddingTop: `${scaleH(24, scale.h)}px`, paddingBottom: `${scaleH(16, scale.h)}px` }}>
        <h1 className="font-bold text-center" style={{ color: localTheme.textColor, fontSize: `${scaleW(36, scale.w)}px` }}>Home Feed</h1>
      </div>

      {/* Main Layout - Three Column Structure */}
      <div ref={swipeRef} className={`mx-auto pb-8 w-full relative ${isMobile ? 'overflow-hidden' : ''}`}>
        {/* LEFT SECTION - Share Button & Post Modal */}
        <div
          className="fixed transition-all duration-300"
          style={{
            left: `${scaleW(24, scale.w)}px`,
            top: `${scaleH(96, scale.h)}px`,
            zIndex: 40,
            opacity: isMobile ? (currentSection === 'left' ? 1 : 0) : 1,
            pointerEvents: isMobile ? (currentSection === 'left' ? 'auto' : 'none') : 'auto'
          }}>
          {leftColumnContent}
        </div>

        {/* MIDDLE SECTION - Main Feed Column - Centered under "Home Feed" title */}
        <div
          className="w-[32vw] max-w-[900px] min-w-[500px] flex flex-col items-center transition-all duration-300"
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 40,
            opacity: isMobile ? (currentSection === 'middle' ? 1 : 0) : 1,
            pointerEvents: isMobile ? (currentSection === 'middle' ? 'auto' : 'none') : 'auto'
          }}>
            {renderCenterColumn()}
        </div>

        {/* RIGHT SECTION - Sidebar Widgets */}
        <div
          className="absolute pt-6 transition-all duration-300"
          style={{
            right: '2rem',
            top: 0,
            opacity: isMobile ? (currentSection === 'right' ? 1 : 0) : 1,
            pointerEvents: isMobile ? (currentSection === 'right' ? 'auto' : 'none') : 'auto'
          }}>
            {renderRightColumn()}
        </div>
      </div>

      {renderModals()}
    </div>
  );
}
